/**
 * Partner online pool — contract line #29 (ADDITIVE v1.8.0, wave 9, T.Y9; D2/A14).
 *
 *   #29 PATCH /api/partners/:partnerId → `{ data: { partner: PartnerDto } }` (ADMIN, audited)
 *
 * There is NO GET and NO list on purpose (SR §3.6): a partner reaches the clients
 * through #6/#7 (`partnerId`/`partnerName`/`partnerPlanOnline`) and the cabinet
 * (#26), and a partner with branches cannot be deleted (`Restrict`). So the pool a
 * settings block edits is seeded from the venue reads, and `used` — the live
 * prefill rows already counted against the pool — travels only on THIS answer.
 * Lowering the pool below `used` is legal by design (the rows stay, NEW ones are
 * refused until the pool catches up), which is exactly the warning the UI prints.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { requestEnvelope } from './client';

/** `PartnerDto` (1:1 with `apps/api/src/partners/dto.ts`). */
export interface PartnerDto {
  /** The key. Absent on a body an older api sent. */
  id?: number;
  /** `academy-top` — the stable human key. */
  code: string;
  name: string;
  /** The SHARED pool — the number the prefill quota check reads and every branch shows. */
  planOnline: number;
  /** Live prefill rows already counted against the pool, across all the partner's branches. */
  used: number;
}

/** Body of #29 — `planOnline` is required and has NO `null`: 0 is the legal empty pool. */
export interface PartnerPatchInput {
  planOnline: number;
}

export function partnerPath(partnerId: number): string {
  return `/partners/${encodeURIComponent(partnerId)}`;
}

/** #29 — the one partner write; the api answers the row with the fresh `used`. */
export async function patchPartnerPool(partnerId: number, planOnline: number, signal?: AbortSignal): Promise<PartnerDto> {
  const envelope = await requestEnvelope<{ partner?: PartnerDto }>('PATCH', partnerPath(partnerId), ['data'], {
    method: 'PATCH',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ planOnline } satisfies PartnerPatchInput),
  });
  const partner = envelope.data?.partner;
  if (!partner) throw new Error('Ответ api не содержит партнёра.');
  return partner;
}

/**
 * The pool write. The venue cache is invalidated alongside: every `PARTNER_ONLINE`
 * row of #6/#7 carries `partnerPlanOnline`, and a stale pool on a venue card would
 * outlive the settings block that just changed it. The api writes the
 * `ENTITY_CHANGE` audit row itself.
 */
export function usePartnerPoolPatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { partnerId: number; planOnline: number }) =>
      patchPartnerPool(input.partnerId, input.planOnline),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['venues'] });
    },
  });
}
